/**
 * ClientActionPlanPage — Client-portal Action Plan viewer / editor
 *
 * Accessible only to CLIENT-type users (enforced by ClientProtectedRoute and
 * the backend portal_router guard).  Scope is automatically filtered to the
 * sites the signed-in client user may access (_resolve_client_site_ids).
 *
 * AP role (server-derived, included in GET /{plan_id} response):
 *   L3  — explicit site assignment → full mutation access
 *   L2  — sub-region assignment → read-only (banner shown, controls hidden)
 *   L1  — group assignment → read-only (banner shown, controls hidden)
 *   NONE — no grant (should not reach detail page; access check blocks earlier)
 *
 * The primary gate is the backend _assert_client_can_mutate_plan guard on
 * every mutation route.  UI hiding is defence-in-depth only.
 *
 * AP Package 3 additions (L3 only):
 *   - Item: holistic_response (L3 editable pre-populated working response)
 *   - Corrective Action: client_completion_status
 *     implemented | partially_implemented | not_implemented
 *   Auditor verification (internal portal only) uses auditor_assessment.
 *
 * What clients can edit (L3 only):
 *   - Item: contributed_factors, due_date, holistic_response
 *   - Corrective Action: description, by_who, by_when, progress_note,
 *     client_completion_status
 *   - Factor lines: add / edit / delete numeric selling-price lines
 * What clients cannot edit (rendered read-only for all roles):
 *   - finding_text (auditor-authored, frozen issued-source context)
 *   - item status (auditor-set)
 *   - auditor_assessment on CAs (internal/auditor only)
 *
 * Routes:
 *   /client/action-plans           — list
 *   /client/action-plans/:planId   — detail
 *
 * PDF: GET /api/client/action-plans/:planId/pdf (tenant-scoped, WeasyPrint)
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Download,
  Loader2,
  Plus,
  Trash2,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { clientActionPlanService } from '../services';
import { getApiError } from '@/lib/utils';
import { useClientPortal } from '../contexts/ClientPortalContext';

const STATUS_LABEL = {
  open: 'To do',
  partially_resolved: 'Partially Resolved',
  resolved: 'Resolved',
};

const SCOPE_LABEL = { BEVERAGE: 'Beverage', FOOD: 'Food', SHOP: 'Shop' };

/**
 * Parse an [Observations] finding_text block into structured sections/rows
 * so they can be rendered as a tidy read-only table.
 *
 * Format written by build_variance_seed_items (ap_snapshot.py):
 *   [Observations] Top stock variances from issued report
 *   Shortages:
 *   • Item — qty × measure @ £price = ±£amount  [optional note]
 *   Surpluses:
 *   • Item — ±£amount  (legacy — no qty/measure/price)
 */
function parseObsFindingText(findingText) {
  if (!findingText) return [];
  // Strip the [Observations] header line
  const body = findingText.replace(/^\[Observations\][^\n]*\n?/, '').trim();
  if (!body) return [];

  const sections = [];
  let current = null;

  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (!line) continue;

    // Section header: "Shortages:" / "Surpluses:"
    if (!line.startsWith('\u2022') && line.endsWith(':')) {
      current = { label: line.slice(0, -1), rows: [] };
      sections.push(current);
      continue;
    }

    // Bullet row
    if (line.startsWith('\u2022') && current) {
      let core = line.slice(1).trim();

      // Extract optional note  [...] at the end (2-space separated)
      let note = '';
      const noteM = core.match(/\s{2}\[([^\]]+)\]\s*$/);
      if (noteM) {
        note = noteM[1];
        core = core.slice(0, core.length - noteM[0].length);
      }

      // Split on em-dash " — " (U+2014)
      const EM = ' \u2014 ';
      const di = core.indexOf(EM);
      if (di === -1) {
        current.rows.push({ item: core, qty: '', measure: '', price: '', amount: '', note });
        continue;
      }

      const item   = core.slice(0, di).trim();
      const detail = core.slice(di + EM.length).trim();

      // Full format: "qty × measure @ £price = ±£amount"
      const fm = detail.match(
        /^([\d.]+)\s*\u00d7\s*(.+?)\s*@\s*([\u00a3$][\d,.]+)\s*=\s*([+\-][\u00a3$][\d,.]+)$/
      );
      if (fm) {
        current.rows.push({ item, qty: fm[1], measure: fm[2].trim(), price: fm[3], amount: fm[4], note });
        continue;
      }

      // No-measure: "qty @ £price = ±£amount"
      const nm = detail.match(
        /^([\d.]+)\s*@\s*([\u00a3$][\d,.]+)\s*=\s*([+\-][\u00a3$][\d,.]+)$/
      );
      if (nm) {
        current.rows.push({ item, qty: nm[1], measure: '', price: nm[2], amount: nm[3], note });
        continue;
      }

      // Legacy / fallback — just the amount string
      current.rows.push({ item, qty: '', measure: '', price: '', amount: detail, note });
    }
  }

  return sections;
}
const STATUS_CLS = {
  open: 'bg-amber-100 text-amber-800 border-amber-200',
  partially_resolved: 'bg-blue-100 text-blue-800 border-blue-200',
  resolved: 'bg-green-100 text-green-800 border-green-200',
};

function ReadOnlyBanner({ apRole }) {
  if (apRole !== 'L1' && apRole !== 'L2') return null;
  const scopeLabel = apRole === 'L1' ? 'Group (portfolio-level)' : 'Regional';
  return (
    <div
      className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm text-amber-800"
      data-testid="ap-readonly-banner"
      role="status"
      aria-live="polite"
    >
      <AlertCircle className="w-4 h-4 shrink-0 text-amber-500" />
      <span>
        <strong>Read-only view</strong> — your access level ({scopeLabel}) allows viewing
        and downloading this Action Plan but not editing it. Contact your site team for updates.
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = STATUS_CLS[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ── List Page ─────────────────────────────────────────────────────────────────
function ClientActionPlanListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const siteIdParam = searchParams.get('site_id');
  const [plans, setPlans]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    const params = siteIdParam ? { site_id: Number(siteIdParam) } : {};
    clientActionPlanService.list(params)
      .then(setPlans)
      .catch(e => setError(getApiError(e)))
      .finally(() => setLoading(false));
  }, [siteIdParam]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 flex items-center gap-2 text-red-600">
        <AlertCircle className="w-5 h-5" /><p>{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="client-ap-list">
      {siteIdParam && (
        <div
          className="flex items-center gap-2 text-sm text-gray-500"
          data-testid="ap-list-site-breadcrumb"
        >
          <button
            type="button"
            onClick={() => navigate(`/client/property-dashboard?site=${siteIdParam}`)}
            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
            data-testid="ap-list-back-to-property-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Property Dashboard
          </button>
          <span>&middot;</span>
          <span>{plans[0]?.site_name ? `Plans for ${plans[0].site_name}` : 'Action Plans'}</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">My Actions</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/client/action-plans/worklist"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3 py-1.5 transition-colors shadow-sm"
            data-testid="ap-list-worklist-link"
          >
            <ClipboardList className="w-3.5 h-3.5" />
            My Actions
          </Link>
          <Link
            to="/client/action-plans/analytics"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-indigo-600 transition-colors"
            data-testid="ap-list-analytics-link"
          >
            <BarChart3 className="w-3.5 h-3.5" />
            Analytics
          </Link>
        </div>
      </div>

      {plans.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center text-sm text-gray-400">
          No action plans yet.
        </div>
      ) : (
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden divide-y">
          {plans.map(p => (
            <div
              key={p.id}
              className="flex items-center px-5 py-3 gap-3 hover:bg-gray-50 cursor-pointer"
              onClick={() => navigate(`/client/action-plans/${p.id}`)}
              data-testid={`client-ap-row-${p.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-800 text-sm">{p.site_name || '—'}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {p.scope}{p.audit_date ? ` · ${new Date(p.audit_date).toLocaleDateString('en-GB')}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {p.source_result_value != null && (
                  <span className={`text-sm font-semibold ${p.source_result_value < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {p.source_result_value > 0 ? '+' : ''}{p.source_result_value}%
                  </span>
                )}
                {p.open_item_count > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-800 rounded px-2 py-0.5" data-testid={`client-plan-outstanding-badge-${p.id}`}>
                    {p.open_item_count} outstanding
                  </span>
                )}
                <StatusBadge status={p.status} />
                <ArrowRight className="w-4 h-4 text-gray-300" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Detail Page ───────────────────────────────────────────────────────────────
function FactorLineRow({ fl, onSave, onDelete, isReadOnly }) {
  const [desc,   setDesc]   = useState(fl.description || '');
  const [price,  setPrice]  = useState(fl.selling_price_value ?? '');
  const [qty,    setQty]    = useState(fl.quantity ?? '');
  const [saving, setSaving] = useState(false);

  const total = parseFloat(price || 0) * parseFloat(qty || 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(fl.id, { description: desc, selling_price_value: price === '' ? null : parseFloat(price), quantity: qty === '' ? null : parseFloat(qty), line_total: qty !== '' && price !== '' ? total : null, version: fl.version });
      toast.success('Line saved');
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 text-sm" data-testid={`fl-row-${fl.id}`}>
      <input
        className="flex-1 border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
        value={desc}
        onChange={e => !isReadOnly && setDesc(e.target.value)}
        placeholder="Description"
        readOnly={isReadOnly}
        data-testid={`fl-desc-${fl.id}`}
      />
      <input
        type="number"
        className="w-20 border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
        value={price}
        onChange={e => !isReadOnly && setPrice(e.target.value)}
        placeholder="£ price"
        readOnly={isReadOnly}
        data-testid={`fl-price-${fl.id}`}
      />
      <input
        type="number"
        className="w-16 border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
        value={qty}
        onChange={e => !isReadOnly && setQty(e.target.value)}
        placeholder="Qty"
        readOnly={isReadOnly}
        data-testid={`fl-qty-${fl.id}`}
      />
      <span className="w-20 text-xs text-right text-gray-600">
        £{isNaN(total) ? '—' : total.toFixed(2)}
      </span>
      {!isReadOnly && (
        <>
          <button onClick={handleSave} disabled={saving} className="text-emerald-600 hover:text-emerald-800 disabled:opacity-40" data-testid={`fl-save-${fl.id}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          </button>
          <button onClick={() => onDelete(fl.id)} className="text-red-400 hover:text-red-600" data-testid={`fl-del-${fl.id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </>
      )}
    </div>
  );
}

function ItemCard({ item, planId, onPlanUpdated, isReadOnly, planResult, planVariance }) {
  // Observation items (seeded from report variance) are displayed as supporting context.
  // Status badge is hidden — they are not independently-closable findings.
  // AP Package 3: is_observation uses stored flag (authoritative) via API.
  const isObs = item.is_observation;
  // Issue 318: collapse toggle removed — content always visible; no expanded state needed.
  const [factors,  setFactors]              = useState(item.contributed_factors || '');
  // AP Package 3 — holistic working response (obs items only, L3-editable)
  const [holistic, setHolistic]             = useState(item.holistic_response || '');
  const [addingCA,     setAddingCA]     = useState(() => (item.corrective_actions || []).length === 0 && !isReadOnly);
  const [newCADesc,    setNewCADesc]    = useState('');
  const [addingCABusy, setAddingCABusy] = useState(false);
  const [savingItem,   setSavingItem]   = useState(false);

  const handleSaveItem = async () => {
    setSavingItem(true);
    try {
      const payload = { contributed_factors: factors, version: item.version };
      // AP Package 3: include holistic_response for obs items
      if (isObs) payload.holistic_response = holistic;
      const updated = await clientActionPlanService.updateItem(planId, item.id, payload);
      onPlanUpdated(updated);
      toast.success('Item saved');
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setSavingItem(false);
    }
  };

  const handleSaveCA = async (caId, data) => {
    const updated = await clientActionPlanService.updateCA(planId, item.id, caId, data);
    onPlanUpdated(updated);
    return updated;
  };

  const handleAddCA = async () => {
    if (!newCADesc.trim()) return;
    setAddingCABusy(true);
    try {
      const updated = await clientActionPlanService.addCA(planId, item.id, { description: newCADesc.trim() });
      onPlanUpdated(updated);
      setNewCADesc('');
      setAddingCA(false);
      toast.success('Corrective action added');
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setAddingCABusy(false);
    }
  };

  const handleAddFL = async () => {
    try {
      const updated = await clientActionPlanService.addFactorLine(planId, item.id, { description: 'New factor', selling_price_value: null, quantity: null, line_total: null });
      onPlanUpdated(updated);
    } catch (e) {
      toast.error(getApiError(e));
    }
  };

  const handleSaveFL = async (flId, data) => {
    const updated = await clientActionPlanService.updateFactorLine(planId, item.id, flId, data);
    onPlanUpdated(updated);
  };

  const handleDeleteFL = async (flId) => {
    try {
      const updated = await clientActionPlanService.deleteFactorLine(planId, item.id, flId);
      onPlanUpdated(updated);
      toast.success('Line removed');
    } catch (e) {
      toast.error(getApiError(e));
    }
  };

  const totalFL = (item.factor_lines || []).reduce((s, fl) => s + (parseFloat(fl.line_total) || 0), 0);

  return (
    <div className="border rounded-xl overflow-hidden" data-testid={`client-ap-item-${item.id}`}>
      {/* Issue 318: collapsible header (finding_text, chevron, parent status) removed.
          Content is always directly visible; finding_text (auditor-authored) is not
          exposed to clients — the Audit Summary table and CA descriptions give context. */}
      <div className="p-4 space-y-4 bg-white">
          {/* Audit Summary — read-only variance table for obs items.
              For current (non-CF) obs items: renders from plan.current_audit_variance
              (structured data from the linked report's sections_json) showing
              Top Shortages for negative results, Top Surpluses for positive.
              For carried-forward obs items: skip — they are historical context,
              the current-audit variance belongs to the current plan only. */}
          {isObs && (() => {
            const isCarryForward = !!item.carry_forward_from_id;
            if (isCarryForward) return null; // historical CF obs — no current-audit variance table

            // Determine which section to surface based on the plan result sign
            const targetKey   = planResult < 0 ? 'shortages' : planResult > 0 ? 'surpluses' : null;
            if (!targetKey) return null; // zero / unknown — show nothing

            // Primary path: use structured variance from the report's sections_json
            const varianceRows = planVariance ? (planVariance[targetKey] || []) : [];

            // Legacy fallback: parse finding_text for older reports that used structured text
            let legacyRows = [];
            if (varianceRows.length === 0) {
              const parsed = parseObsFindingText(item.finding_text);
              const targetLabel = targetKey === 'shortages' ? 'Shortages' : 'Surpluses';
              const sec = parsed.find(s => s.label === targetLabel);
              if (sec) legacyRows = sec.rows;
            }

            const useVariance = varianceRows.length > 0;
            const useLegacy   = !useVariance && legacyRows.length > 0;
            const labelText   = targetKey === 'shortages' ? 'Shortages' : 'Surpluses';
            const labelCls    = targetKey === 'shortages' ? 'text-red-600' : 'text-green-700';

            return (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`obs-table-${item.id}`}>
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">Audit Summary</p>
                <p className={`text-xs font-semibold mb-1 ${labelCls}`}>Top {labelText}</p>
                {useVariance ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">Product</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 w-28">Amount</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">Auditor Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {varianceRows.map((row, ri) => {
                          const amt = row.at_retail;
                          const fmtAmt = amt == null
                            ? '—'
                            : `${amt < 0 ? '-' : '+'}£${Math.round(Math.abs(amt)).toLocaleString('en-GB')}`;
                          const amtCls = amt < 0 ? 'text-red-600' : amt > 0 ? 'text-green-700' : 'text-gray-700';
                          return (
                            <tr key={ri} className="border-t">
                              <td className="px-2 py-1.5 text-xs text-gray-800">{row.item_name}</td>
                              <td className={`px-2 py-1.5 text-xs font-medium tabular-nums ${amtCls}`}>{fmtAmt}</td>
                              <td className="px-2 py-1.5 text-xs text-gray-500 italic">{row.optional_note || ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : useLegacy ? (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">Product</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 w-28">Amount</th>
                          <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">Auditor Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {legacyRows.map((row, ri) => (
                          <tr key={ri} className="border-t">
                            <td className="px-2 py-1.5 text-xs text-gray-800">{row.item}</td>
                            <td className={`px-2 py-1.5 text-xs font-medium tabular-nums ${row.amount?.startsWith('-') ? 'text-red-600' : row.amount?.startsWith('+') ? 'text-green-700' : 'text-gray-700'}`}>
                              {row.amount || '—'}
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-500 italic">{row.note || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            );
          })()}

          {/* Contributing Factors textarea — non-obs context items only.
              Obs items: the Audit Summary table above provides the context;
              the contributed_factors narrative is preserved in the DB but
              not surfaced here to avoid duplication (Issue 318). */}
          {!isObs && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Contributing Factors
              </label>
              <textarea
                className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
                rows={3}
                value={factors}
                onChange={e => !isReadOnly && setFactors(e.target.value)}
                readOnly={isReadOnly}
                placeholder="Describe contributing factors…"
                data-testid={`item-factors-${item.id}`}
              />
            </div>
          )}
          {!isObs && !isReadOnly && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleSaveItem}
                disabled={savingItem}
                data-testid={`item-save-${item.id}`}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {savingItem ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Save
              </Button>
            </div>
          )}

          {/* Corrective Actions — hidden when empty and read-only (suppresses "No corrective
              actions recorded." placeholder for L1/L2 portal users viewing completed items) */}
          {(!isReadOnly || (item.corrective_actions || []).length > 0) && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-600">
                Corrective Actions{(item.corrective_actions || []).length > 0 && ` (${item.corrective_actions.length})`}
              </p>
              {!isReadOnly && (
                <button
                  className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                  onClick={() => setAddingCA(v => !v)}
                  data-testid={`ca-add-btn-${item.id}`}
                >
                  <Plus className="w-3 h-3" /> Add Action
                </button>
              )}
            </div>
            {(item.corrective_actions || []).length === 0 && !addingCA && (
              <p className="text-xs text-gray-400 italic">
                {isReadOnly ? 'No corrective actions recorded.' : 'No corrective actions yet. Add one above.'}
              </p>
            )}
            <div className="space-y-3">
              {(item.corrective_actions || []).map(ca => (
                <CAEditRow key={ca.id} ca={ca} itemId={item.id} planId={planId} onSave={handleSaveCA} isReadOnly={isReadOnly} />
              ))}
            </div>
            {!isReadOnly && addingCA && (
              <div className="border rounded-lg p-3 bg-emerald-50/50 mt-2 space-y-2">
                <textarea
                  className="w-full border rounded px-2 py-1.5 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  rows={2}
                  placeholder="e.g. Re-train bar team on correct measures before next service — all staff briefed and signed off by manager"
                  value={newCADesc}
                  onChange={e => setNewCADesc(e.target.value)}
                  data-testid={`ca-new-desc-${item.id}`}
                />
                <div className="flex gap-2">
                  <button
                    className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded font-medium disabled:opacity-50"
                    onClick={handleAddCA}
                    disabled={addingCABusy || !newCADesc.trim()}
                    data-testid={`ca-new-save-${item.id}`}
                  >
                    {addingCABusy ? 'Saving…' : 'Add'}
                  </button>
                  <button
                    className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded"
                    onClick={() => { setAddingCA(false); setNewCADesc(''); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Factor Lines — hidden when empty and read-only (suppresses "No factor lines
              recorded." placeholder for L1/L2 portal users viewing completed items).
              For obs items in the client portal: only show when factor lines exist
              (these are internal audit price-evidence records; the client doesn't add them). */}
          {(isObs
              ? (item.factor_lines || []).length > 0
              : (!isReadOnly || (item.factor_lines || []).length > 0)
          ) && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-600">{isObs ? 'Supporting Evidence' : 'Factor Lines'}</p>
              {!isReadOnly && (
                <button
                  className="text-xs text-emerald-600 hover:text-emerald-800 flex items-center gap-1"
                  onClick={handleAddFL}
                  data-testid={`fl-add-${item.id}`}
                >
                  <Plus className="w-3 h-3" /> Add line
                </button>
              )}
            </div>
            {(item.factor_lines || []).length === 0 ? (
              <p className="text-xs text-gray-400 italic" data-testid={`fl-empty-${item.id}`}>
                {isReadOnly ? 'No factor lines recorded.' : 'No factor lines. Add selling-price evidence above.'}
                {item.carry_forward_from_stock_number && ' Factor lines are period-specific evidence and are not copied when a finding is carried forward.'}
              </p>
            ) : (
              <div className="space-y-1.5">
                <div className="flex gap-2 text-xs font-medium text-gray-400 px-0">
                  <span className="flex-1">Description</span>
                  <span className="w-20">Price (£)</span>
                  <span className="w-16">Qty</span>
                  <span className="w-20 text-right">Total</span>
                  {!isReadOnly && <span className="w-10" />}
                </div>
                {(item.factor_lines || []).map(fl => (
                  <FactorLineRow key={fl.id} fl={fl} onSave={handleSaveFL} onDelete={handleDeleteFL} isReadOnly={isReadOnly} />
                ))}
                <div className="flex justify-end border-t pt-1">
                  <span className="text-xs font-semibold text-gray-700">
                    Grand Total: £{Math.round(Math.abs(totalFL)).toLocaleString('en-GB')}
                  </span>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
    </div>
  );
}

function CAEditRow({ ca, itemId, planId, onSave, isReadOnly }) {
  const [desc,     setDesc]     = useState(ca.description || '');
  const [byWho,    setByWho]    = useState(ca.by_who || '');
  const [byWhen,   setByWhen]   = useState(ca.by_when ? ca.by_when.slice(0, 10) : '');
  const [progress, setProgress] = useState(ca.progress_note || '');
  // AP Package 3 — client completion status
  const [ccs,      setCcs]      = useState(ca.client_completion_status || '');
  const [saving,   setSaving]   = useState(false);

  // Reseed local state only when a DIFFERENT CA is loaded into this row
  // (ca.id changes).  Deliberately omit ca.version so a post-save
  // version bump does NOT re-seed: if the server response carried a
  // stale by_when (async flush timing edge case) the effect would
  // overwrite the user's typed value with the old date.  The backend's
  // preserve-on-null PATCH semantics protect fields the user left blank.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setDesc(ca.description || '');
    setByWho(ca.by_who || '');
    setByWhen(ca.by_when ? ca.by_when.slice(0, 10) : '');
    setProgress(ca.progress_note || '');
    setCcs(ca.client_completion_status || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ca.id]);

  const whenRef = useRef(null);

  const handleSave = async () => {
    // Read the date straight from the input so a browser that skipped the
    // change event cannot silently send an empty target date.
    const el = whenRef.current;
    if (el && el.validity && el.validity.badInput) {
      toast.error('Target date is incomplete — enter day, month and year, or clear it.');
      return;
    }
    const whenValue = (el && el.value) || byWhen || '';
    if (whenValue !== byWhen) setByWhen(whenValue);
    setSaving(true);
    try {
      const payload = {
        description: desc, by_who: byWho, by_when: whenValue || null,
        progress_note: progress, version: ca.version,
      };
      // AP Package 3: only send client_completion_status if it changed
      if (ccs !== (ca.client_completion_status || '')) {
        payload.client_completion_status = ccs || null;
      }
      const updated = await onSave(ca.id, payload);
      // Read-back: confirm the server actually stored the date we sent.
      const savedCa = (updated?.items || []).flatMap(it => it.corrective_actions || []).find(c => c.id === ca.id);
      const savedWhen = savedCa?.by_when ? savedCa.by_when.slice(0, 10) : '';
      if (whenValue && savedCa && savedWhen !== whenValue) {
        toast.error(`Target date did not save (server has ${savedWhen || 'no date'}). Please re-enter it.`);
        setByWhen(savedWhen);
        return;
      }
      toast.success(whenValue ? `Corrective action saved — target date ${whenValue.split('-').reverse().join('/')}` : 'Corrective action saved');
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // AP Package 3 — derive lifecycle display state
  const completionLabel = {
    implemented:           'Implemented',
    partially_implemented: 'Partially Implemented',
    not_implemented:       'Not Implemented',
  };
  const completionColor = {
    implemented:           'text-green-700 bg-green-50 border-green-200',
    partially_implemented: 'text-amber-700 bg-amber-50 border-amber-200',
    not_implemented:       'text-red-700 bg-red-50 border-red-200',
  };

  return (
    <div className="bg-gray-50 rounded-lg p-3 space-y-2" data-testid={`ca-row-${ca.id}`}>
      <textarea
        className="w-full border rounded px-2 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
        rows={3}
        value={desc}
        onChange={e => !isReadOnly && setDesc(e.target.value)}
        readOnly={isReadOnly}
        placeholder="e.g. Retrain bar team on stock rotation procedure by end of next week — all team briefed and signed off by manager"
        data-testid={`ca-desc-${ca.id}`}
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
          value={byWho}
          onChange={e => !isReadOnly && setByWho(e.target.value)}
          readOnly={isReadOnly}
          placeholder="By who"
          data-testid={`ca-who-${ca.id}`}
        />
        <input
          type="date"
          className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
          ref={whenRef}
          value={byWhen}
          onChange={e => !isReadOnly && setByWhen(e.target.value)}
          onBlur={e => !isReadOnly && e.target.value !== byWhen && setByWhen(e.target.value)}
          readOnly={isReadOnly}
          aria-label="Target date"
          data-testid={`ca-when-${ca.id}`}
        />
      </div>
      <textarea
        className="w-full border rounded px-2 py-1 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500"
        rows={2}
        value={progress}
        onChange={e => !isReadOnly && setProgress(e.target.value)}
        readOnly={isReadOnly}
        placeholder="Progress note…"
        data-testid={`ca-progress-${ca.id}`}
      />

      {/* Headline classification (Issue 6) + overdue warning — replaces competing
          "Open / Not done / Awaiting / Pending verification" labels. */}
      {(() => {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const byWhenDate = ca.by_when ? (() => { const d = new Date(ca.by_when); d.setHours(0,0,0,0); return d; })() : null;
        const caIsOverdue = byWhenDate && byWhenDate < today;
        const isVerified    = ca.auditor_assessment === 'implemented';
        const isAwaiting    = ca.client_completion_status === 'implemented' && !isVerified;
        const isCarried     = ca.auditor_assessment === 'carry_forward';
        const isInProgress  = ca.client_completion_status === 'in_progress' && !isVerified && !isAwaiting;
        return (
          <div className="flex items-center gap-2 flex-wrap">
            {isVerified ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full border text-green-700 bg-green-50 border-green-200" data-testid={`ca-headline-${ca.id}`}>
                Verified complete
              </span>
            ) : isAwaiting ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full border text-amber-700 bg-amber-50 border-amber-200" data-testid={`ca-headline-${ca.id}`}>
                Completed and awaiting auditor review
              </span>
            ) : isCarried ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full border text-purple-700 bg-purple-50 border-purple-200" data-testid={`ca-headline-${ca.id}`}>
                Carried forward
              </span>
            ) : isInProgress ? (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full border text-sky-700 bg-sky-50 border-sky-200" data-testid={`ca-headline-${ca.id}`}>
                In Progress
              </span>
            ) : (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full border text-blue-700 bg-blue-50 border-blue-200" data-testid={`ca-headline-${ca.id}`}>
                To do
              </span>
            )}
            {caIsOverdue && !isVerified && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full border text-red-700 bg-red-50 border-red-200" data-testid={`ca-overdue-badge-${ca.id}`}>
                Overdue
              </span>
            )}
          </div>
        );
      })()}

      {/* AP Package 3 — Client completion status controls */}
      {!isReadOnly && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Completion Status</label>
          <select
            className="border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 w-full"
            value={ccs}
            onChange={e => setCcs(e.target.value)}
            data-testid={`ca-completion-status-${ca.id}`}
          >
            <option value="">— Not reported —</option>
            <option value="in_progress">In Progress</option>
            <option value="implemented">Implemented</option>
            <option value="partially_implemented">Partially Implemented</option>
            <option value="not_implemented">Not Implemented</option>
          </select>
        </div>
      )}

      {/* Read-only: show completion detail (suppressed if system actor) */}
      {isReadOnly && ca.client_completion_status && (
        <div className="flex items-center gap-2 flex-wrap">
          {ca.completion_invalidated_at && (
            <span className="text-xs text-red-500 italic" data-testid={`ca-invalidated-${ca.id}`}>
              Invalidated by later edit
            </span>
          )}
          {/* Issue 5: suppress system-generated actor labels */}
          {ca.client_completed_by_name && !/\[(FIXTURE|SYNTHETIC|AUTOMATED|SYSTEM)\]/i.test(ca.client_completed_by_name) && (
            <span className="text-xs text-gray-400" data-testid={`ca-completed-by-${ca.id}`}>by {ca.client_completed_by_name}</span>
          )}
        </div>
      )}

      {/* Auditor assessment (read-only — set by auditor via internal portal) */}
      {ca.auditor_assessment && (
        <div className="text-xs text-gray-500 border-t pt-2 mt-1">
          <span className="font-medium">Auditor assessment:</span>{' '}
          <span data-testid={`ca-auditor-assessment-${ca.id}`}>
            {ca.auditor_assessment === 'implemented' ? 'Verified complete'
              : ca.auditor_assessment === 'not_implemented' ? 'Not done'
              : ca.auditor_assessment === 'partially_implemented' ? 'Partially done'
              : ca.auditor_assessment.replace(/_/g, ' ')}
          </span>
          {ca.auditor_assessment_note && (
            <p className="text-gray-400 italic mt-0.5">{ca.auditor_assessment_note}</p>
          )}
        </div>
      )}

      {!isReadOnly && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            data-testid={`ca-save-${ca.id}`}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
            Save
          </Button>
        </div>
      )}
    </div>
  );
}

function ClientActionPlanDetailPage() {
  const { planId } = useParams();
  const navigate   = useNavigate();
  const [plan,      setPlan]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [pdfBusy,   setPdfBusy]   = useState(false);
  const [submitBusy, setSubmitBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    clientActionPlanService.get(planId)
      .then(setPlan)
      .catch(e => setError(getApiError(e)))
      .finally(() => setLoading(false));
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const handlePlanUpdated = (updated) => setPlan(updated);

  // Server-derived capability role.  L3 = site team (can edit).
  // L1/L2 = portfolio/regional viewer (read-only, banner shown).
  const apRole     = plan?.ap_role || 'NONE';
  const isReadOnly = apRole === 'L1' || apRole === 'L2';

  const handleSubmitPlan = async () => {
    // Guard: each CA must have a non-empty owner and a target date.
    // Response-first submission is preserved: no client_completion_status
    // or all-implemented gate is applied here.
    const missingCAs = (plan?.items || []).flatMap(it =>
      (it.corrective_actions || [])
        .filter(ca => !(ca.by_who && ca.by_who.trim()) || !ca.by_when)
        .map(ca =>
          `"${(it.finding_text || '').slice(0, 40)}": ${(ca.description || '').slice(0, 40)}`
        )
    );
    if (missingCAs.length > 0) {
      toast.error(
        `Each corrective action needs an owner and target date before submission. ` +
        `Missing: ${missingCAs.slice(0, 3).join('; ')}${missingCAs.length > 3 ? '…' : ''}`
      );
      return;
    }
    setSubmitBusy(true);
    try {
      const updated = await clientActionPlanService.submitPlan(planId);
      setPlan(updated);
      const rev = updated.plan_submission_revision || 1;
      toast.success(`Plan submitted (Revision ${rev})`);
    } catch (e) {
      toast.error(getApiError(e, 'Submission failed'));
    } finally {
      setSubmitBusy(false);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    toast.info('Generating PDF…', { id: 'ap-pdf', duration: 30000 });
    try {
      const blob = await clientActionPlanService.downloadPdf(planId);
      const url  = window.URL.createObjectURL(new Blob([blob]));
      const a    = document.createElement('a');
      a.href     = url;
      const _site  = (plan?.site_name || '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const _scope = (plan?.scope || '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const _date  = plan?.audit_date ? plan.audit_date.slice(0, 10).replace(/-/g, '') : '';
      a.download = [_site, _scope, _date].filter(Boolean).join('-') + '.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded', { id: 'ap-pdf' });
    } catch (e) {
      toast.error(getApiError(e, 'PDF download failed'), { id: 'ap-pdf' });
    } finally {
      setPdfBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 flex items-center gap-2 text-red-600">
        <AlertCircle className="w-5 h-5" /><p>{error}</p>
      </div>
    );
  }

  const items = plan?.items || [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="client-ap-detail">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/client/action-plans')} className="text-gray-400 hover:text-gray-700">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Action Plan</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-0.5">
            <div>
              <p className="text-xs text-gray-400">Property</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{plan.site_name || '—'}</p>
            </div>
            {plan.client_name && (
              <div>
                <p className="text-xs text-gray-400">Client</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{plan.client_name}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-gray-400">Service</p>
              <p className="text-sm font-semibold text-gray-900">
                {SCOPE_LABEL[plan.scope] || plan.scope || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Period</p>
              <p className="text-sm font-semibold text-gray-900">
                {plan.audit_date ? new Date(plan.audit_date).toLocaleDateString('en-GB') : '—'}
              </p>
            </div>
            {/* Issue 5/318: stock_number reference hidden from client portal — internal traceability only */}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {plan.source_result_value != null && (
            <span className={`text-sm font-semibold ${plan.source_result_value < 0 ? 'text-red-600' : 'text-green-600'}`}>
              {plan.source_result_value > 0 ? '+' : ''}{plan.source_result_value}%
            </span>
          )}
          <StatusBadge status={plan.status} />
          <Button
            size="sm"
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={pdfBusy}
            data-testid="client-ap-pdf-btn"
            className="flex items-center gap-1"
          >
            {pdfBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Read-only banner for L1/L2 users */}
      <ReadOnlyBanner apRole={apRole} />

      {/* AP Package 4 — Plan-level holistic response submission panel.
          Shown ABOVE the result snapshot so the submission state is prominent.
          - L3 non-submitted: submit button to record the plan's response.
          - L3 submitted: badge showing revision + submitter (re-submit allowed).
          - L1/L2: no controls; badge shown if submitted (read-only context). */}
      {(() => {
        const isSubmitted = plan.plan_submission_status === 'submitted';
        const submittedBy = plan.plan_submitted_by_name;
        const revision    = plan.plan_submission_revision;
        const submittedAt = plan.plan_submitted_at;
        const fmtD = d => {
          if (!d) return '';
          try {
            return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
          } catch { return d; }
        };
        return (
          <div
            className={`rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
              isSubmitted
                ? 'bg-blue-50 border-blue-200'
                : 'bg-gray-50 border-gray-200'
            }`}
            data-testid="plan-submission-panel"
          >
            <div className="flex items-center gap-3">
              {isSubmitted ? (
                <>
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-100 border border-blue-300 px-2.5 py-1 rounded-full"
                    data-testid="plan-submitted-badge"
                  >
                    Plan Submitted
                  </span>
                  <span className="text-xs text-blue-600">
                    {submittedBy ? `by ${submittedBy}` : ''}{submittedAt ? ` on ${fmtD(submittedAt)}` : ''}
                    {revision ? ` · Revision ${revision}` : ''}
                  </span>
                </>
              ) : (
                <div>
                  <p className="text-sm font-medium text-gray-700">Action Plan Response — Not yet submitted</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Complete your corrective actions above, then submit when ready for review.
                  </p>
                </div>
              )}
            </div>
            {!isReadOnly && (
              <Button
                size="sm"
                variant={isSubmitted ? 'outline' : 'default'}
                onClick={handleSubmitPlan}
                disabled={submitBusy}
                data-testid="plan-submit-btn"
                className={isSubmitted ? 'shrink-0' : 'shrink-0 bg-blue-600 hover:bg-blue-700 text-white'}
              >
                {submitBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                {isSubmitted ? 'Re-submit' : 'Submit Plan'}
              </Button>
            )}
          </div>
        );
      })()}

      {/* Result snapshot — identical surface to Internal page and PDF.
          Values are read from the issued Summary Report that triggered this
          AP; '—' means the source field was absent at publication.  Nothing
          is re-entered or recomputed here. */}
      {(() => {
        const snap = plan.result_snapshot || {};
        const rc  = snap.result_cash;
        const rp  = snap.result_pct ?? plan.source_result_value;
        const yc  = snap.ytd_cash;
        const yp  = snap.ytd_pct;
        const sh  = snap.stockholding;
        const yld = snap.yield_pct;
        const ytdYield = snap.ytd_yield_pct;
        const periodDays = plan.stock_period_days;  // derived: report column or (audit−prev).days
        const prevAudit  = plan.previous_audit_date; // start of stock period from report
        const fmtD = d => {
          if (!d) return null;
          const [y, m, dy] = d.slice(0, 10).split('-').map(Number);
          return new Date(y, m - 1, dy).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        };
        const periodLabel = periodDays == null ? '—'
          : (prevAudit && plan.audit_date
              ? `${fmtD(prevAudit)} \u2013 ${fmtD(plan.audit_date)} (${periodDays} days)`
              : `${periodDays} days`);

        const fmtCash = v => v == null ? '—' : `${v < 0 ? '-' : '+'}£${Math.round(Math.abs(v)).toLocaleString('en-GB')}`;
        const fmtPct  = v => v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(2)}%`;

        const allFL = (items || []).flatMap(it => it.factor_lines || []).filter(fl => fl.line_total != null);
        const flTotal = allFL.length ? allFL.reduce((s, fl) => s + fl.line_total, 0) : null;
        // Shared formula with backend compute_unexplained_balance:
        //   signed cash result + explained selling-value total (deficit only).
        const unexplained = rc != null && rc < 0 && flTotal != null ? rc + flTotal : null;

        const SCOPE_LABEL = { BEVERAGE: 'Beverage', FOOD: 'Food', SHOP: 'Shop' };

        return (
          <div className="bg-white border rounded-xl overflow-hidden text-sm shadow-sm" data-testid="client-ap-result-snapshot">
            {/* Property / audit context header */}
            <div className="bg-gray-50 border-b px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-0.5">
              <span className="text-sm font-semibold text-gray-800">{plan.site_name || '—'}</span>
              {plan.audit_date && (
                <span className="text-xs text-gray-500">{fmtD(plan.audit_date)}</span>
              )}
              {plan.scope && (
                <span className="text-xs text-gray-400">{SCOPE_LABEL[plan.scope] || plan.scope}</span>
              )}
              {rp != null && (
                <span className={`text-xs font-bold ml-auto tabular-nums ${rp < 0 ? 'text-red-700' : rp > 0 ? 'text-green-700' : 'text-gray-600'}`}>
                  {fmtPct(rp)}
                </span>
              )}
            </div>

            {/* Primary: Current Period + YTD (prominent) */}
            <div className="grid grid-cols-2 divide-x border-b">
              <div className="px-4 py-3 text-center">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">Current Period</p>
                <p className={`text-2xl font-bold tabular-nums leading-tight ${rc != null && rc < 0 ? 'text-red-600' : rc != null && rc > 0 ? 'text-green-600' : 'text-gray-700'}`}>
                  {fmtCash(rc)}
                </p>
                <p className={`text-sm font-medium mt-0.5 ${rc != null && rc < 0 ? 'text-red-500' : rc != null && rc > 0 ? 'text-green-600' : 'text-gray-500'}`}>{fmtPct(rp)}</p>
              </div>
              <div className="px-4 py-3 text-center">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-0.5">YTD</p>
                <p className={`text-2xl font-bold tabular-nums leading-tight ${yc != null && yc < 0 ? 'text-red-600' : yc != null && yc > 0 ? 'text-green-600' : 'text-gray-700'}`}>
                  {fmtCash(yc)}
                </p>
                <p className={`text-sm font-medium mt-0.5 ${yc != null && yc < 0 ? 'text-red-500' : yc != null && yc > 0 ? 'text-green-600' : 'text-gray-500'}`}>{fmtPct(yp)}</p>
              </div>
            </div>

            {/* Supporting metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x bg-gray-50 border-b">
              <div className="px-3 py-2 text-center">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Stockholding</p>
                <p className="text-base font-semibold text-gray-700">{fmtCash(sh)}</p>
              </div>
              <div className="px-3 py-2 text-center">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Yield</p>
                <p className="text-base font-semibold text-gray-700">{fmtPct(yld)}</p>
              </div>
              <div className="px-3 py-2 text-center">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">YTD Yield</p>
                <p className="text-base font-semibold text-gray-700" data-testid="client-ap-ytd-yield">{fmtPct(ytdYield)}</p>
              </div>
              <div className="px-3 py-2 text-center">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Period</p>
                <p className="text-base font-semibold text-gray-700" data-testid="client-ap-period-days">{periodLabel}</p>
              </div>
            </div>

            {/* Factor lines & unexplained balance (conditional) */}
            {(flTotal != null || unexplained != null) && (
              <div className="grid grid-cols-2 gap-0 divide-x">
                {flTotal != null && (
                  <div className="px-3 py-2">
                    <p className="text-xs text-gray-400 font-medium">Explained (Factor Lines)</p>
                    <p className="text-sm font-semibold text-gray-700">+£{Math.round(Math.abs(flTotal)).toLocaleString('en-GB')}</p>
                  </div>
                )}
                {unexplained != null && (
                  <div className="px-3 py-2">
                    <p className="text-xs text-gray-400 font-medium">Unexplained Balance</p>
                    <p className={`text-sm font-semibold ${unexplained < 0 ? 'text-red-600' : 'text-green-600'}`} data-testid="client-ap-unexplained-balance">{fmtCash(unexplained)}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Items */}
      {/* Issue 318: filter items that have no client-editable content.
          Observation items always show (they have the variance summary).
          Non-obs items with no CAs, no factor lines, no holistic_response and
          no contributed_factors are test/placeholder items (e.g. "Permission
          test finding") and must not be exposed to the client portal. */}
      {(() => {
        const visible = items.filter(item =>
          item.is_observation ||
          (item.corrective_actions?.length > 0) ||
          (item.factor_lines?.length > 0) ||
          item.holistic_response ||
          item.contributed_factors
        );
        if (visible.length === 0) return (
          <div className="bg-white border rounded-xl p-8 text-center text-sm text-gray-400">
            No findings on this action plan.
          </div>
        );
        return (
          <div className="space-y-3">
            {/* "Findings (N)" heading removed — Issue 318; items are directly visible */}
            {visible.map(item => (
              <ItemCard
                key={item.id}
                item={item}
                planId={planId}
                onPlanUpdated={handlePlanUpdated}
                isReadOnly={isReadOnly}
                planResult={plan.source_result_value}
                planVariance={plan.current_audit_variance}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}

// ── Route-driven export ───────────────────────────────────────────────────────
export default function ClientActionPlanPage() {
  const { planId } = useParams();
  const { apEnabled } = useClientPortal();
  const navigate = useNavigate();

  // Direct-URL guard: redirect to dashboard when AP is disabled for this customer.
  // Wait for branding to resolve (null = loading) before redirecting.
  useEffect(() => {
    if (apEnabled === false) navigate('/client/dashboard', { replace: true });
  }, [apEnabled, navigate]);

  // Render nothing while the AP gate is still loading to avoid flash of
  // content before a potential redirect.
  if (apEnabled === null) return null;

  return planId ? <ClientActionPlanDetailPage /> : <ClientActionPlanListPage />;
}
